import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import TaskDrawer from "../../components/task/TaskDrawer";
import { TYPE, PRIO, typeTitle } from "../../design/ui";
import {
  STATUS_CODE,
  PRIO_CODE,
  PRIO_LABEL,
  statusKey,
  normalizeTask,
  normalizeProject,
} from "../../design/domain";
import { useApp } from "../../design/AppContext";
import { apiPostForm, currentAccount } from "../../utils/api";
import { apiErrorMessage } from "../../utils/apiError";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Banner, StatusChip } from "../../components/mui";
import { PageLoading } from "../../components/loading";
import { MdChevronLeft, MdWarningAmber } from "react-icons/md";

/**
 * 看板 — 照抄設計稿 el-pm-template_4.html:861-939。
 *
 * 欄位是一排等寬的 flex 欄（窄螢幕改成上下疊），收合的「已完成」欄固定 220px。
 * Layout 的內容區所有路由一律撐滿寬度、不設上限，
 * 所以這裡不自己算整塊看板的寬度。
 */

/** 欄位定義 — 設計稿只畫四欄（L876-935）；第五種狀態「已關閉」併進「已完成」欄 */
const KB_DEFS = [
  ["pending", "待處理"],
  ["progress", "進行中"],
  ["review", "審核中"],
  ["done", "已完成"],
];

/**
 * 後端 board_column 仍然是五種狀態，照常收下；差別只在前端要畫成幾欄。
 * 「已關閉」與「已完成」對使用者是同一件事（這件事結束了），分兩欄只是把
 * 畫面切窄、又逼人去分辨兩個很像的詞，所以顯示時合併，欄頭再寫清楚含幾件已關閉。
 */
const MERGE_TO = { closed: "done" };
const colKeyOf = (k) => MERGE_TO[k] || k;

/** 後端沒設定 board_column.wip_limit 時的預設（設計稿：進行中 4 件） */
const WIP_LIMIT = { progress: 4 };

/** 每欄一次先畫幾張卡；其餘由欄底的「再看 N 件」逐段追加 */
const PAGE_SIZE = 10;

/** cellPages 取不到值時的保底，避免 render 對 undefined 取屬性 */
const EMPTY_CELL = { items: [], pageItems: [], total: 0, totalPages: 1, page: 1, rest: 0 };

const ALL = "all";
const NONE = "__none__";
/** 「只看誰」下拉裡代表登入者自己的值（原本是獨立的「只看我的」開關） */
const MINE = "__mine__";

/** 空欄只寫一句話說明它什麼時候會有東西，不畫空表格（設計稿 L921） */
const EMPTY_TEXT = {
  pending: "尚未開始的工作將顯示於此。",
  progress: "開始進行的工作將顯示於此。",
  review: "送審的工作將顯示於此。",
  done: "已完成的工作將顯示於此。",
};

/* eslint-disable no-unused-vars */
/**
 * 以下兩個常數對應的工具列控制項暫時不顯示 —— 設計稿的看板工具列只留兩個
 * 白話下拉（L865-870：「看哪個專案」「只看」）。
 *
 * 常數與下面的分組邏輯（lanes / epicOf）都原樣留著：泳道與型別篩選是既有功能，
 * 不是這次改版要拿掉的東西，只是暫時沒有位置。要放回工具列時把 swim / typeSel
 * 改回 useState 再接上這兩個常數就好，不必重寫一次分組。
 */
const TYPE_ORDER = ["epic", "story", "subtask", "bug", "tech", "explore", "task"];

/** 泳道模式（暫不使用，見上） */
const SWIM_OPTS = [
  { v: "none", l: "不分泳道" },
  { v: "assignee", l: "依指派人" },
  { v: "prio", l: "依優先級" },
  { v: "epic", l: "依大項目" },
];
/* eslint-enable no-unused-vars */

const PRIO_ORDER = ["urgent", "high", "medium", "low"];

/** 樂觀覆寫表的鍵：後端沒給 issue_key 時 code 會是 #undefined，優先用 id 才不會整批一起被搬動 */
const keyOf = (t) => String(t.id || t.code);

/** 欄位物件用來裝卡片的鍵，順序即優先序 */
const COL_ITEM_KEYS = ["items", "Items", "tasks", "Tasks", "cards"];

/**
 * 卡片在同一欄裡的排序尺規 —— 必須與後端一致。
 * BoardService.GetColumns 是 `OrderByDescending(Priority).ThenByDescending(Id)`
 * （緊急優先、同級由新到舊）排完，再依狀態把卡片分進各欄，
 * 所以「每一欄」看到的就是這個順序。
 * 移動後要算出卡片會插在目標欄的第幾個位置（＝要不要先追加卡片才看得到），只能用這把尺：
 * 前端的 allCards 是一欄接一欄串起來的（見 toCards），照它的下標算會落在完全不同的段落。
 *
 * Id 遞增＝建立順序，所以由大到小就是由新到舊。
 */
const cardOrder = (a, b) => {
  const pa = PRIO_CODE[a.prio] !== undefined ? PRIO_CODE[a.prio] : PRIO_CODE.medium;
  const pb = PRIO_CODE[b.prio] !== undefined ? PRIO_CODE[b.prio] : PRIO_CODE.medium;
  if (pa !== pb) return pb - pa;
  return (Number(b.id) || 0) - (Number(a.id) || 0);
};

/**
 * 後端 /Board/Columns 回 { projectId, projectName, columns:[{ key,label,wipLimit,count,items }] }，
 * 但這支仍在演進中，過去也出現過另外兩種形狀，因此三種都吸收並攤平成「已決定狀態」的工項陣列：
 * ① 欄位陣列 [{ status, items:[…] }]、② 工項平陣列 [task]、③ 以狀態為鍵的物件 { pending:[…] }。
 * 任何一種都對不上時回空陣列，讓畫面退化成空欄位而不是白畫面。
 */
function toCards(raw) {
  if (!raw) return [];

  // ③ 物件：先剝掉常見的外層包裝，再以鍵當狀態
  if (!Array.isArray(raw)) {
    const inner = raw.columns || raw.Columns || raw.data_list || raw.list;
    if (inner) return toCards(inner);
    return Object.keys(raw).reduce((acc, k) => {
      const arr = raw[k];
      if (!Array.isArray(arr)) return acc;
      const key = statusKey(k);
      // normalizeTask 的預設參數擋不住 null（只有 undefined 會套預設），陣列裡混到 null 會整頁炸掉
      return acc.concat(arr.map((t) => ({ ...normalizeTask(t || {}), status: key })));
    }, []);
  }

  return raw.reduce((acc, row) => {
    if (!row) return acc;
    const itemKey = COL_ITEM_KEYS.find((k) => k in row);
    // ① 欄位物件：欄位自己的狀態優先於卡片上的狀態
    if (itemKey !== undefined) {
      const items = row[itemKey];
      /* 帶了 items 卻不是陣列（空欄位被回成 null）＝這一欄沒有卡片。
         不擋掉的話會掉進 ②，把「欄位設定物件」正規化成一張沒有標題的幽靈卡 */
      if (!Array.isArray(items)) return acc;
      const key = statusKey(row.status !== undefined ? row.status : row.key || row.name || row.Status);
      return acc.concat(items.map((t) => ({ ...normalizeTask(t || {}), status: key })));
    }
    // ② 直接就是工項
    return acc.concat(normalizeTask(row));
  }, []);
}

/**
 * 取出後端的欄位設定（自訂欄名與 WIP 上限）。
 * 專案可以在 board_column 自訂欄位，這裡以狀態鍵對回固定的欄位；
 * 同一狀態被設成多欄時以第一個為準，不然會出現兩個同名欄位互搶卡片。
 * 「已關閉」的設定照樣收下，只是前端沒有那一欄可以顯示它。
 */
function toColMeta(raw) {
  const arr = _.get(raw, "columns") || _.get(raw, "Columns");
  if (!Array.isArray(arr)) return {};
  return arr.reduce((acc, c) => {
    if (!c || typeof c !== "object") return acc;
    const k = statusKey(c.key !== undefined ? c.key : c.status !== undefined ? c.status : c.name);
    if (acc[k]) return acc;
    const wip = c.wipLimit !== undefined ? c.wipLimit : c.wip_limit;
    acc[k] = {
      label: c.label || c.name || "",
      wip: wip === null || wip === undefined || wip === "" ? null : Number(wip),
    };
    return acc;
  }, {});
}

/** 專案清單可能是陣列，也可能是 { data_list } 分頁包裝 */
function toProjects(raw) {
  const arr = Array.isArray(raw) ? raw : _.get(raw, "data_list", []);
  if (!Array.isArray(arr)) return [];
  return arr.filter(Boolean).map((p) => normalizeProject(p));
}

/** 是否今天到期（逾期由下面的 isLate 判斷，這裡只管「就是今天」） */
function isDueToday(v) {
  if (!v) return false;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return false;
  const n = new Date();
  return (
    d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate()
  );
}

/**
 * 真的逾期 ＝ 到期日已經過了「今天」這一天。
 *
 * 不能直接用 normalizeTask 的 overdue：它是 `due_date < 現在時刻`
 * （design/domain.js 的 isOverdue），而 due_date 存的是當天 00:00，
 * 所以今天到期的工項在今天早上八點就會被判成逾期 —— 同一張卡的 meta 行寫著
 * 「今天 9/20」、徽章卻喊已經遲了，而使用者剛剛在「今天」頁看到的是「今天要交」。
 * 語意與 pages/MyTask/Index.js 的 isLate 一致，同一件工項在兩頁必須講一樣的話。
 */
function isLate(t) {
  return !!t.overdue && !isDueToday(t.dueRaw);
}

/**
 * 到期日寫成看得懂的一句（設計稿 L884「9 月 24 日」、L906「今天 9/20」）。
 * 沒有日期就明講「沒有期限」——留空白會讓人以為是資料沒載到。
 */
function dueText(v) {
  if (!v) return "未設期限";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "未設期限";
  if (isDueToday(v)) return `今天 ${d.getMonth() + 1}/${d.getDate()}`;
  const md = `${d.getMonth() + 1} 月 ${d.getDate()} 日`;
  return d.getFullYear() === new Date().getFullYear() ? md : `${d.getFullYear()} 年 ${md}`;
}

/**
 * 看板卡片 — 設計稿 L881-886：型別徽章、標題、一行「負責人 · 到期日」。
 *
 * 卡片本身可以拖（滑鼠），但拖拉不是無障礙操作，所以卡內一定要有帶文字的按鈕：
 * 「打開這一件」給鍵盤使用者進詳情，「移到…」給鍵盤與觸控使用者換狀態。
 */
function KanbanCard({ t, showProject, settled, dragging, menuOpen, moveTargets, onOpen, onToggleMenu, onMove, onDragStart, onDragEnd }) {
  const type = TYPE[t.type] || TYPE.task;
  /* 已經結束的卡片不再提醒到期。
     normalizeTask 的 overdue 已經排除了 done/closed，但樂觀移動時 t.status 還是舊的
     （moved 覆寫只改欄位歸屬），所以兩支期限判斷都再用 settled 擋一次，
     否則剛拖進「已完成」的卡片還會在原地喊已逾期。 */
  const today = !settled && isDueToday(t.dueRaw);
  const late = !settled && isLate(t);
  const attention = late || today;
  const moveBtnRef = useRef(null);
  const menuId = `kb-move-${t.id || t.code}`;
  const who = t.assignee || "未指派";
  const meta = [who, dueText(t.dueRaw)];
  // 看「全部專案」時，不寫專案名就分不出這張卡是誰家的；選了單一專案就是多餘的雜訊
  if (showProject && t.projectName) meta.push(t.projectName);

  return (
    <Card
      component="article"
      draggable
      data-tour="kanban-card"
      onDragStart={(e) => {
        onDragStart(t);
        // Firefox 必須設定 dataTransfer 才會真的啟動拖曳
        if (e.dataTransfer) {
          e.dataTransfer.effectAllowed = "move";
          e.dataTransfer.setData("text/plain", t.code || "");
        }
      }}
      onDragEnd={onDragEnd}
      sx={{
        p: "var(--space-4)",
        // 可以拖就要看得出可以拖
        cursor: "grab",
        opacity: dragging ? 0.4 : undefined,
        // 左側 4px 警示邊，與清單列（RowButton tone="attention"）同一種 inset box-shadow
        boxShadow: attention ? (th) => `inset 4px 0 0 ${th.palette.error.main}` : undefined,
      }}
    >
      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", mb: "var(--space-2)" }}>
        <StatusChip kind="type" size="small" title={typeTitle(t.type)}>
          {type.label}
        </StatusChip>
        {/* 優先級只在「緊急」時出現：那是要插隊的訊號，其餘等級寫出來只是每張卡都多一個標籤 */}
        {t.prio === "urgent" && (
          <StatusChip kind="warn" size="small">
            {PRIO_LABEL.urgent}
          </StatusChip>
        )}
      </Stack>

      <Typography sx={{ fontWeight: 600 }}>{t.name}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: "var(--space-1)" }}>
        {meta.join(" · ")}
      </Typography>

      {attention && (
        <Box sx={{ mt: "var(--space-3)" }}>
          <StatusChip kind="attention" mark={<MdWarningAmber />}>
            {/* 先判「今天」再判「逾期」：overdue 是拿 00:00 跟此刻比，
                今天到期的工項整個上班時段都會是 true，讓它搶先就等於全庫只有這裡
                把今天要交的說成已經遲了。用詞也統一成全庫一致的「已逾期」 */}
            {today ? "今日到期" : "已逾期"}
          </StatusChip>
        </Box>
      )}

      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", mt: "var(--space-3)" }}>
        <Button variant="text" onClick={() => onOpen(t)}>
          開啟
        </Button>
        <Button
          ref={moveBtnRef}
          variant="text"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-controls={menuOpen ? menuId : undefined}
          onClick={() => onToggleMenu(t)}
        >
          移至…
        </Button>
      </Stack>

      {/* 換狀態的選單：MUI Menu 負責定位、點外面關閉、ESC 與方向鍵移動焦點 */}
      <Menu
        id={menuId}
        anchorEl={moveBtnRef.current}
        open={menuOpen && Boolean(moveBtnRef.current)}
        onClose={() => onToggleMenu(t)}
        slotProps={{ list: { "aria-label": "移至欄位" } }}
      >
        {moveTargets.map((c) => (
          <MenuItem key={c.key} onClick={() => onMove(t, c.key, c.label)}>
            {c.label}
          </MenuItem>
        ))}
      </Menu>
    </Card>
  );
}

KanbanCard.propTypes = {
  t: PropTypes.object.isRequired,
  showProject: PropTypes.bool,
  settled: PropTypes.bool,
  dragging: PropTypes.bool,
  menuOpen: PropTypes.bool,
  moveTargets: PropTypes.array,
  onOpen: PropTypes.func,
  onToggleMenu: PropTypes.func,
  onMove: PropTypes.func,
  onDragStart: PropTypes.func,
  onDragEnd: PropTypes.func,
};
KanbanCard.defaultProps = {
  showProject: false,
  settled: false,
  dragging: false,
  menuOpen: false,
  moveTargets: [],
  onOpen: () => {},
  onToggleMenu: () => {},
  onMove: () => {},
  onDragStart: () => {},
  onDragEnd: () => {},
};

function Board(props) {
  const navigate = useNavigate();
  /* 點卡片改成在本頁展開抽屜看完整內容，而不是跳到專案詳情頁再跳回來 */
  const [drawerTask, setDrawerTask] = useState(null);
  const { toast } = useApp();
  /* 初值 true：useEffect 在首次 paint 之後才跑，初值 false 會先閃一格空狀態 */
  const [loading, setLoading] = useState(true);
  /** 樂觀更新：移動後先在前端覆寫狀態，等 API 成功重抓後再清掉；失敗則回滾 */
  const [moved, setMoved] = useState({});
  /** 用 ref 存拖曳中的卡片本體（drop 時要同步讀得到），另用 state 存 key 才能畫出半透明回饋 */
  const dragRef = useRef(null);
  const [dragKey, setDragKey] = useState(null);
  /** 目前游標所在的投放格：`${laneId}|${colKey}` */
  const [overCell, setOverCell] = useState(null);
  /** 目前哪一張卡把「移到…」選單打開了（同時只開一個，卡片才不會整片變成按鈕牆） */
  const [moveKey, setMoveKey] = useState(null);

  /* 篩選與檢視偏好——依規範只放在元件 state，不落地。
     但初值要吃得下網址：專案詳情的「看板」分頁是帶 ?projectId= 過來的
     （pages/Project/Detail.js 的 goTab），不讀的話使用者按了「看板」會掉到
     「全部專案」，連頁首的返回鈕都變成回不去那個專案的「‹ 回到專案」。
     只當初值（lazy initializer），之後使用者自己切下拉時網址不會再回頭覆蓋他。 */
  const [searchParams] = useSearchParams();
  const [projSel, setProjSel] = useState(() => {
    /* 只收數字：reload() 會把它 Number() 成 payload，亂填的值會變成 NaN ——
       那會同時送出「不帶 projectId 的請求」與「篩掉所有卡片的前端過濾」，
       使用者看到的是一片空看板而不是錯誤 */
    const pid = searchParams.get("projectId");
    return pid && Number(pid) > 0 ? pid : ALL;
  });
  const [assigneeSel, setAssigneeSel] = useState(ALL);

  /*
   * 搜尋關鍵字、型別、泳道這三個條件的控制項暫時不顯示（設計稿的工具列只有兩個下拉），
   * 固定成「不篩」。篩選與分組的程式碼都留在下面，要放回工具列時改回 useState 即可。
   */
  const q = "";
  const typeSel = ALL;
  const swim = "none";

  /*
   * 第四欄（已完成，含已關閉）預設收合 —— 設計稿 L926-933。
   *
   * 看板要回答的是「現在手上有什麼、誰在做」，做完的是結果不是待辦。
   * 已完成光一個專案就有二十幾張卡，全部攤開會把待處理與進行中擠到畫面外。
   * 收合時欄內一張卡都不放，只留件數與一顆「展開這一欄」。
   */
  const [doneOpen, setDoneOpen] = useState(false);

  /**
   * 每個「格子」（`${泳道id}|${欄位key}`）目前已經追加到第幾段。
   * 用單一個物件保存而不是每欄一個 hook —— 欄位×泳道的數量會隨資料變動，
   * 在 map 裡呼叫 hook 會讓每次 render 的 hook 數量不同，React 會直接壞掉。
   * 這裡只存「使用者按過幾次『再看』」，超出範圍時在算 cellPages 時才夾住，
   * 不寫回 state：資料短暫變少（移動、重抓）不會把使用者展開的段落洗掉。
   */
  const [colPage, setColPage] = useState({});

  const me = useMemo(() => currentAccount(), []);

  const reload = useCallback(
    (done) => {
      const pid = projSel === ALL ? null : Number(projSel);
      props.GET_BoardColumns(pid ? { projectId: pid } : {}, done, setLoading);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projSel]
  );

  useEffect(() => {
    props.GET_ProjectList({ Page: 0, PageCount: 0 }, null, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* 換專案要重抓，並丟掉上一個專案殘留的樂觀覆寫。
     「只看誰」的選項是從「目前看板上的卡片」推導的，換專案後多半不存在了——
     不一併清掉的話，下拉會顯示成空白而且整個看板被篩成 0 張，看起來像壞掉。 */
  useEffect(() => {
    setMoved({});
    setAssigneeSel(ALL);
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projSel]);

  const projects = useMemo(() => toProjects(props.projectList), [props.projectList]);
  const allCards = useMemo(() => toCards(props.columns), [props.columns]);
  const colMeta = useMemo(() => toColMeta(props.columns), [props.columns]);

  /** 目前選到的專案（沒選或清單還沒回來就是 undefined，標題與返回鈕都要吃得住） */
  const proj = useMemo(
    () => projects.find((p) => String(p.id) === String(projSel)),
    [projects, projSel]
  );

  /**
   * 專案切換是伺服器端過濾（重抓），但重抓回來前畫面上還是舊資料，
   * 這裡再做一次前端過濾，避免切換瞬間閃出別的專案的卡片。
   */
  const boardCards = useMemo(
    () =>
      projSel === ALL ? allCards : allCards.filter((t) => String(t.projectId) === String(projSel)),
    [allCards, projSel]
  );

  /** 生效狀態＝樂觀覆寫優先 */
  const eff = useCallback((t) => moved[keyOf(t)] || t.status, [moved]);

  /* 「只看誰」的選項由目前看板上的卡片推導（不打 /Auth/Admin/List——那支需要 admin 權限） */
  const assigneeOpts = useMemo(() => {
    const map = new Map();
    let hasNone = false;
    boardCards.forEach((t) => {
      const acc = t.assigneeAccount || "";
      if (!acc && !t.assignee) {
        hasNone = true;
        return;
      }
      const key = acc || t.assignee;
      if (!map.has(key)) map.set(key, t.assignee || key);
    });
    const list = [...map.entries()]
      .map(([account, name]) => ({ account, name }))
      .sort((a, b) => String(a.name).localeCompare(String(b.name), "zh-Hant"));
    return hasNone ? [...list, { account: NONE, name: "未指派" }] : list;
  }, [boardCards]);

  /* 型別下拉的選項（暫不顯示，見檔頭 TYPE_ORDER 的說明；邏輯留著） */
  // eslint-disable-next-line no-unused-vars
  const typeOpts = useMemo(() => {
    const extra = _.uniq(boardCards.map((t) => t.type)).filter((k) => !TYPE_ORDER.includes(k));
    return [...TYPE_ORDER, ...extra];
  }, [boardCards]);

  const hasFilter = !!q.trim() || assigneeSel !== ALL || typeSel !== ALL;

  const cards = useMemo(() => {
    let arr = boardCards;
    if (typeSel !== ALL) arr = arr.filter((t) => t.type === typeSel);
    if (assigneeSel === MINE && me)
      arr = arr.filter((t) => String(t.assigneeAccount || "").toLowerCase() === me.toLowerCase());
    else if (assigneeSel === NONE) arr = arr.filter((t) => !t.assigneeAccount && !t.assignee);
    else if (assigneeSel !== ALL)
      arr = arr.filter((t) => (t.assigneeAccount || t.assignee) === assigneeSel);
    const kw = q.trim().toLowerCase();
    if (kw)
      arr = arr.filter((t) =>
        `${t.name} ${t.code} ${t.assignee} ${t.projectName}`.toLowerCase().includes(kw)
      );
    return arr;
  }, [boardCards, typeSel, assigneeSel, me, q]);

  /* 換篩選條件時把每一欄收回第一段：
     卡片組成整個變了，還停在展開很多段的狀態看到的是完全不相干的一片。
     （移動與重抓不會經過這裡，那兩種情況要保留使用者展開的段落） */
  useEffect(() => {
    setColPage((s) => (Object.keys(s).length ? {} : s));
    // 這些值只當「變更訊號」用，effect 內沒有讀取，關掉 exhaustive-deps 的多餘相依警告
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assigneeSel, projSel]);

  /** 直接指定要展開到第幾段（移動後把目標欄追加到卡片所在段落用） */
  const setCellPage = useCallback((cellId, next) => {
    setColPage((s) => (s[cellId] === next ? s : { ...s, [cellId]: next }));
  }, []);

  /**
   * 相對追加（「再看 N 件」）。基準取自 state 而不是 render 當下的 cell.page——
   * 連續快按兩下時兩次 click 共用同一個 render 的閉包，讀 cell.page 會只前進一段。
   */
  const stepCellPage = useCallback((cellId, delta, totalPages) => {
    setColPage((s) => {
      const base = Math.min(Math.max(1, Number(s[cellId]) || 1), totalPages);
      const next = Math.min(Math.max(1, base + delta), totalPages);
      return s[cellId] === next ? s : { ...s, [cellId]: next };
    });
  }, []);

  const cols = useMemo(
    () =>
      KB_DEFS.map(([key, defLabel]) => {
        const meta = colMeta[key];
        const items = cards.filter((t) => colKeyOf(eff(t)) === key);
        // WIP 是真實的產能約束，要看整欄實際卡片數，不能因為前端篩掉幾張就變成沒超限
        const real = boardCards.filter((t) => colKeyOf(eff(t)) === key).length;
        /* 後端有回這一欄的設定就完全照它（含「刻意不設上限」的 null），
           只有整批欄位設定都拿不到時（舊的回傳形狀）才退回設計稿預設，
           否則專案把「進行中」的 wip_limit 清掉後，畫面還是會誤報超限 */
        const wip = meta
          ? (meta.wip != null && !Number.isNaN(meta.wip) ? meta.wip : null)
          : WIP_LIMIT[key] || null;
        return {
          key,
          label: (meta && meta.label) || defLabel,
          items,
          count: items.length,
          real,
          // 併進來的已關閉件數，欄頭要寫清楚，否則使用者會覺得件數多得莫名其妙
          closed: key === "done" ? items.filter((t) => eff(t) === "closed").length : 0,
          wip,
          /* 到達上限就要提醒，不必等超過：WIP 的意思是「這麼多就夠了」，
             第 4 件開始做的時候講才來得及，第 5 件已經接下去了才講沒有用 */
          over: !!wip && real >= wip,
        };
      }),
    [cards, boardCards, colMeta, eff]
  );

  /* 以 id 建索引：泳道「依大項目」要沿 parentTaskId 往上找，被篩掉的父階也得查得到 */
  const byId = useMemo(() => {
    const m = new Map();
    allCards.forEach((t) => {
      if (t.id != null) m.set(String(t.id), t);
    });
    return m;
  }, [allCards]);

  /** 沿 parent 往上找第一個大項目；自己就是大項目則回自己。加 guard 防資料成環時無限迴圈 */
  const epicOf = useCallback(
    (t) => {
      if (t.type === "epic") return t;
      let cur = t;
      for (let i = 0; i < 12; i += 1) {
        if (!cur || cur.parentId == null) return null;
        const p = byId.get(String(cur.parentId));
        if (!p || p.id === cur.id) return null;
        if (p.type === "epic") return p;
        cur = p;
      }
      return null;
    },
    [byId]
  );

  /* 泳道分組。目前 swim 固定是 "none"（工具列沒有這個下拉），所以只會走第一行；
     其餘分法留著不刪，見檔頭 SWIM_OPTS 的說明。 */
  const lanes = useMemo(() => {
    if (swim === "none") return [{ id: "__all__", label: "", items: cards }];

    const groups = new Map();
    const push = (id, label, order, meta, t) => {
      if (!groups.has(id)) groups.set(id, { id, label, order, meta, items: [] });
      groups.get(id).items.push(t);
    };

    cards.forEach((t) => {
      if (swim === "assignee") {
        const acc = t.assigneeAccount || t.assignee;
        if (!acc) push(NONE, "未指派", "", { kind: "assignee", name: "" }, t);
        else push(acc, t.assignee || acc, t.assignee || acc, { kind: "assignee", name: t.assignee || acc }, t);
      } else if (swim === "prio") {
        const k = PRIO[t.prio] ? t.prio : "medium";
        push(k, PRIO_LABEL[k] || k, PRIO_ORDER.indexOf(k), { kind: "prio", prio: k }, t);
      } else {
        const e = epicOf(t);
        if (!e) push(NONE, "未歸屬大項目", "", { kind: "epic" }, t);
        else push(`e${e.id}`, e.name || e.code, e.name || e.code, { kind: "epic", code: e.code }, t);
      }
    });

    // 「還沒有人負責 / 沒有歸屬」一律排最後，其餘依優先級順位或名稱排
    return [...groups.values()].sort((a, b) => {
      if (a.id === NONE) return 1;
      if (b.id === NONE) return -1;
      if (swim === "prio") return a.order - b.order;
      return String(a.order).localeCompare(String(b.order), "zh-Hant");
    });
  }, [cards, swim, epicOf]);

  /**
   * 把每條泳道的卡片一次分進各欄（`${泳道id}|${欄位key}` → 卡片陣列）。
   * 用單趟 forEach 建索引而不是每欄各 filter 一次：資料量大時後者是 4×N 次比對。
   * 只依賴資料與分法，展開段落不會讓它重算。
   */
  const cellItems = useMemo(() => {
    const m = {};
    lanes.forEach((lane) => {
      const byCol = {};
      lane.items.forEach((t) => {
        const k = colKeyOf(eff(t));
        if (!byCol[k]) byCol[k] = [];
        byCol[k].push(t);
      });
      cols.forEach((c) => {
        m[`${lane.id}|${c.key}`] = byCol[c.key] || [];
      });
    });
    return m;
  }, [lanes, cols, eff]);

  /**
   * 依 colPage 切出每格目前要畫的卡片。
   * 和舊版的「第幾頁」不同：這裡是累加的 —— 按一次「再看 N 件」就多顯示一段，
   * 已經看過的卡片不會消失。翻頁器把人丟到「第 3 頁」時，前兩頁的卡片就找不到了，
   * 而看板的動作是「掃過去找那一張」，不是「逐頁閱讀」。
   */
  const cellPages = useMemo(() => {
    const m = {};
    Object.keys(cellItems).forEach((id) => {
      const items = cellItems[id];
      const total = items.length;
      const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
      const page = Math.min(Math.max(1, Number(colPage[id]) || 1), totalPages);
      const shown = Math.min(total, page * PAGE_SIZE);
      m[id] = {
        items,
        total,
        totalPages,
        page,
        pageItems: items.slice(0, shown),
        rest: total - shown,
      };
    });
    return m;
  }, [cellItems, colPage]);

  /**
   * 打開卡片：在本頁展開抽屜看完整內容，而不是跳到專案詳情頁再跳回來。
   * 看板的使用情境是「掃視整片工作」，跳頁會把使用者的捲動位置與篩選狀態都弄丟。
   */
  const openDetail = (t) => {
    if (!t || !t.id) return;
    setDrawerTask(t);
  };

  /**
   * 換狀態：先樂觀覆寫 → 打 /Board/Move → 成功重抓、失敗回滾並顯示後端擋下的原因。
   * 拖拉與卡片上的「移到…」選單都走這一支，兩條路的結果才不會長得不一樣。
   *
   * 這裡直接用 apiPostForm 而不是 dispatch POST_BoardMove，因為 saga 的錯誤處理會吞掉例外
   * （只跳 window.alert），頁面拿不到失敗事件就無法回滾，卡片會停在錯誤的欄位。
   * 後端 POST 一律 [FromForm]，apiPostForm 會把物件轉成 FormData。
   */
  const moveCard = async (t, key, label, fallbackLaneId) => {
    if (!t) return;
    const from = eff(t);
    /* 同一欄就不必打 API。比的是「畫面上的欄」：已關閉的卡片被丟進已完成欄時，
       視覺上根本沒有移動，卻會被當成一次 closed → done 的狀態變更 */
    if (colKeyOf(from) === key || !t.id) return;

    /* 目標欄有很多卡時，這一張可能落在還沒展開的段落——使用者放了手卻看不到卡片，
       會以為移動失效。所以要把「卡片真正會出現的那一格」展開到它所在的段落。
       來源欄不動：它只是少一張卡。

       泳道是依指派人／優先級／大項目分的，跟狀態無關 —— 卡片改狀態後仍留在自己那條泳道，
       未必是滑鼠放開的那一條，所以要展開的是卡片自己的泳道×目標欄那一格。 */
    const dk = keyOf(t);
    const ownLane = lanes.find((l) => l.items.some((x) => keyOf(x) === dk));
    const dstCell = `${ownLane ? ownLane.id : fallbackLaneId}|${key}`;
    // 失敗要還原，先記下展開前的值（undefined 代表使用者根本沒展開過這一欄）
    const prevPage = colPage[dstCell];
    /* 目標格現有的卡片已經是後端順序，用同一把尺找出這張卡插進去的位置。
       （不能拿它在來源欄的下標充數：前端陣列是一欄接一欄串的，兩者對不上） */
    const rest = (cellItems[dstCell] || []).filter((x) => keyOf(x) !== dk);
    const at = rest.findIndex((x) => cardOrder(t, x) < 0);
    setCellPage(dstCell, Math.floor((at < 0 ? rest.length : at) / PAGE_SIZE) + 1);
    /* 已完成欄預設是收合的，移進去之後整欄看不見 —— 跟上面展開段落是同一個理由，
       使用者要看得到自己剛做的事發生了什麼 */
    if (key === "done") setDoneOpen(true);

    setMoved((s) => ({ ...s, [dk]: key }));
    try {
      await apiPostForm("/Board/Move", { taskId: t.id, status: STATUS_CODE[key] });
      toast(`已移至「${label}」`);
      reload(() => {
        // 伺服器資料已回填，覆寫值功成身退
        setMoved((s) => _.omit(s, [dk]));
      });
    } catch (err) {
      // 工作流守門擋下（DoD 未完成、需先通過審核…）是預期行為，把原因原話帶給使用者
      setMoved((s) => _.omit(s, [dk]));
      /* 卡片回到原欄，剛才為了露出它而展開的段落也要一起還原，
         否則使用者會被丟在目標欄攤開九十張卡的畫面前 */
      setColPage((s) => {
        if (prevPage === undefined) return dstCell in s ? _.omit(s, [dstCell]) : s;
        return s[dstCell] === prevPage ? s : { ...s, [dstCell]: prevPage };
      });
      toast(apiErrorMessage(err, "移動失敗"), "error");
    }
  };

  const handleDrop = (key, label, laneId) => (e) => {
    e.preventDefault();
    const t = dragRef.current;
    dragRef.current = null;
    setDragKey(null);
    setOverCell(null);
    moveCard(t, key, label, laneId);
  };

  const onCellDragOver = (cellId) => (e) => {
    e.preventDefault();
    // dragover 每幾十毫秒就觸發一次，值沒變時回傳同一個 state，React 會略過重繪
    setOverCell((s) => (s === cellId ? s : cellId));
  };

  /** 「移到…」選單一次只開一個 */
  const toggleMenu = (t) => setMoveKey((s) => (s === keyOf(t) ? null : keyOf(t)));

  /**
   * 欄頭的第二行。WIP 上限寫成整句「已經 4 件，做完再接新的」——
   * 寫成 4/4 的人要先知道斜線兩邊各是什麼意思，而這句話直接說了該怎麼做。
   */
  const headMeta = (c) => {
    if (c.over) return `已達 ${c.real} 件，請先完成再接新工作`;
    if (c.key === "done" && c.closed > 0) return `${c.count} 件（含已關閉 ${c.closed} 件）`;
    return `${c.count} 件`;
  };

  /** 一張卡可以移到哪幾欄＝除了它現在所在的那一欄 */
  const moveTargets = (colKey) =>
    cols.filter((c) => c.key !== colKey).map((c) => ({ key: c.key, label: c.label }));

  const renderCards = (c, cell, laneId) =>
    cell.pageItems.map((t) => (
      <KanbanCard
        key={`${t.id || ""}-${t.code}`}
        t={t}
        showProject={projSel === ALL}
        settled={c.key === "done"}
        dragging={dragKey === keyOf(t)}
        menuOpen={moveKey === keyOf(t)}
        moveTargets={moveTargets(c.key)}
        onOpen={openDetail}
        onToggleMenu={toggleMenu}
        onMove={(x, key, label) => {
          setMoveKey(null);
          moveCard(x, key, label, laneId);
        }}
        onDragStart={(x) => {
          dragRef.current = x;
          setDragKey(keyOf(x));
        }}
        onDragEnd={() => {
          dragRef.current = null;
          setDragKey(null);
          setOverCell(null);
        }}
      />
    ));

  /* ---------- 一欄 ---------- */
  const renderCol = (c, laneId) => {
    const cellId = `${laneId}|${c.key}`;
    const cell = cellPages[cellId] || EMPTY_CELL;
    const hot = overCell === cellId;
    const collapsed = c.key === "done" && !doneOpen;
    const drop = {
      onDragOver: onCellDragOver(cellId),
      onDrop: handleDrop(c.key, c.label, laneId),
      /* 投放目標的回饋用 outline 補。
         不用 border 或 box-shadow 才不會動到欄頭與卡片原本的外觀 */
      style: hot ? { outline: "2px solid var(--primary)", outlineOffset: "4px" } : undefined,
    };

    /* 收合的欄：只有一張說明卡，欄內不放任何卡片（設計稿 L926-933）。
       欄名照常橫寫 —— 直排中文在大字模式與長欄名下會整個爆開 */
    if (collapsed) {
      return (
        <Box
          component="section"
          key={c.key}
          data-tour="kanban-col"
          sx={{ flex: { xs: "1 1 auto", md: "0 0 220px" }, width: { xs: "100%", md: "auto" } }}
          {...drop}
        >
          <Card sx={{ p: "var(--space-4)" }}>
            <Typography variant="h3" component="p">
              {c.label}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: "var(--space-1)" }}>
              {c.closed > 0 ? `${c.count} 件 · 已關閉 ${c.closed} 件` : `${c.count} 件`}
            </Typography>
            <Box sx={{ mt: "var(--space-3)" }}>
              <Button variant="text" onClick={() => setDoneOpen(true)}>
                展開此欄
              </Button>
            </Box>
          </Card>
        </Box>
      );
    }

    return (
      <Box
        component="section"
        key={c.key}
        data-tour="kanban-col"
        sx={{ flex: { xs: "1 1 auto", md: "1 1 0" }, minWidth: 0, width: { xs: "100%", md: "auto" } }}
        {...drop}
      >
        <Box sx={{ px: "var(--space-4)", py: "var(--space-3)", bgcolor: "var(--surface-sunken)", borderRadius: "var(--radius-md)" }}>
          <Typography variant="h3" component="p">
            {c.label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {headMeta(c)}
          </Typography>
          {c.key === "done" && (
            <Box sx={{ mt: "var(--space-2)" }}>
              <Button variant="text" onClick={() => setDoneOpen(false)}>
                收合此欄
              </Button>
            </Box>
          )}
        </Box>

        {cell.total === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: "var(--space-4)" }}>
            {EMPTY_TEXT[c.key] || "目前沒有卡片。"}
          </Typography>
        ) : (
          <Stack spacing={2} sx={{ mt: "var(--space-3)" }}>
            {renderCards(c, cell, laneId)}
            {cell.rest > 0 && (
              <div>
                <Button variant="text" onClick={() => stepCellPage(cellId, 1, cell.totalPages)}>
                  {`顯示更多（${Math.min(PAGE_SIZE, cell.rest)}）`}
                </Button>
              </div>
            )}
          </Stack>
        )}
      </Box>
    );
  };

  const showEmpty = !loading && cards.length === 0;
  const scopeLabel = projSel === ALL ? "全部專案" : (proj ? proj.name : "");

  return (
    /* 這一頁不自己包寬度上限：Layout 的內容區本來就撐滿寬度，
       看板才有整個視窗的寬度可以分四欄 */
    <Stack spacing={2}>
      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
        <Button
          variant="text"
          startIcon={<MdChevronLeft aria-hidden="true" />}
          onClick={() => navigate(proj ? `/Project/Detail/${proj.id}` : "/Project/List")}
        >
          {proj ? `返回${proj.name}` : "返回專案"}
        </Button>
      </Stack>

      {/* 專案名是後端來的真資料；還沒載到就只寫「看板」，不要湊一個名字上去 */}
      <Typography variant="h1" component="h1">
        {scopeLabel ? `看板 · ${scopeLabel}` : "看板"}
      </Typography>

      {/* 工具列收斂成兩個白話下拉（設計稿 L865-870） */}
      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
        <Typography component="label" variant="body2" color="text.secondary" htmlFor="board-proj">
          專案：
        </Typography>
        <TextField
          select
          id="board-proj"
          size="small"
          value={projSel}
          onChange={(e) => setProjSel(e.target.value)}
          slotProps={{ select: { native: true } }}
          sx={{ width: "auto" }}
        >
          <option value={ALL}>全部專案</option>
          {/* 網址指名的專案還沒出現在選項裡時（專案清單還沒回來），先補一個同值的選項 ——
              否則下拉會是一片空白，但看板其實已經篩成那個專案了，
              使用者看到的清單跟下拉說的不是同一件事 */}
          {projSel !== ALL && !projects.some((p) => String(p.id) === String(projSel)) ? (
            <option value={projSel}>專案名稱載入中…</option>
          ) : null}
          {projects.map((p) => (
            <option key={p.id} value={String(p.id)}>
              {p.name}
            </option>
          ))}
        </TextField>

        <Typography component="label" variant="body2" color="text.secondary" htmlFor="board-who">
          負責人：
        </Typography>
        <TextField
          select
          id="board-who"
          size="small"
          value={assigneeSel}
          onChange={(e) => setAssigneeSel(e.target.value)}
          slotProps={{ select: { native: true } }}
          sx={{ width: "auto" }}
        >
          <option value={ALL}>全部成員</option>
          {/* 取不到登入者時就不給這個選項，免得選了之後整片被篩成空的 */}
          {me && <option value={MINE}>我負責的</option>}
          {assigneeOpts.map((a) => (
            <option key={a.account} value={a.account}>
              {a.name}
            </option>
          ))}
        </TextField>
      </Stack>

      {/* 操作說明寫成整句提示條，不寫成灰字小字（設計稿 L872-874） */}
      <Banner kind="info">
        將卡片拖曳至其他欄位即可變更狀態；如不便拖曳，也可點選卡片上的「移至…」變更。
        若被工作流程限制，卡片將自動退回原欄位並顯示原因。
      </Banner>

      {loading && cards.length === 0 ? (
        <PageLoading text="看板載入中…" />
      ) : showEmpty ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h2" component="p">
            {hasFilter ? "沒有符合條件的卡片" : "此看板尚無工項"}
          </Typography>
          <Typography sx={{ mt: 1 }}>
            {hasFilter ? "請選擇其他負責人，或將「負責人」改回「全部成員」。" : "請選擇其他專案，或至「工項」新增工作。"}
          </Typography>
        </Box>
      ) : (
        lanes.map((lane) => (
          <Box
            key={lane.id}
            sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: "var(--space-4)", alignItems: { xs: "stretch", md: "flex-start" } }}
          >
            {cols.map((c) => renderCol(c, lane.id))}
          </Box>
        ))
      )}

      {/* 工項抽屜：在本頁展開完整內容（優先級、標籤、DoD 進度、估時都在裡面），
          與簽核中心同一個元件 */}
      <TaskDrawer
        open={!!drawerTask}
        taskId={drawerTask?.id}
        task={drawerTask}
        projectName={drawerTask?.projectName}
        onClose={() => setDrawerTask(null)}
        onChanged={() => reload()}
        onOpenTask={(id) => setDrawerTask({ id })}
      />
    </Stack>
  );
}

const mapStateToProps = (state) => ({
  columns: _.get(state, "Board.Columns", []),
  projectList: _.get(state, "Project.List", []),
});

const mapDispatchToProps = (dispatch) => ({
  GET_BoardColumns: (payload, callback, loading) =>
    dispatch({ type: "GET_BoardColumns", payload, callback, loading }),
  GET_ProjectList: (payload, callback, loading) =>
    dispatch({ type: "GET_ProjectList", payload, callback, loading }),
});

Board.propTypes = {
  columns: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  projectList: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  GET_BoardColumns: PropTypes.func,
  GET_ProjectList: PropTypes.func,
};

Board.defaultProps = {
  columns: [],
  projectList: [],
  GET_BoardColumns: () => {},
  GET_ProjectList: () => {},
};

export default connect(mapStateToProps, mapDispatchToProps)(Board);
